import { forwardRef, useEffect, useImperativeHandle, useId, useRef, useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";

const loadYouTubeApi = () => {
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (window.__steelMoraYouTubeApiPromise) return window.__steelMoraYouTubeApiPromise;

  window.__steelMoraYouTubeApiPromise = new Promise((resolve, reject) => {
    const priorCallback = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      priorCallback?.();
      resolve(window.YT);
    };
    if (!document.querySelector("script[src='https://www.youtube.com/iframe_api']")) {
      const script = document.createElement("script");
      script.src = "https://www.youtube.com/iframe_api";
      script.async = true;
      script.onerror = () => reject(new Error("YouTube IFrame API unavailable"));
      document.body.appendChild(script);
    }
  });
  return window.__steelMoraYouTubeApiPromise;
};

const YouTubePracticePlayer = forwardRef(({ videoId, onReady, onStateChange, onError, variant = "dark" }, ref) => {
  const hostId = `yt-practice-${useId().replace(/:/g, "")}`;
  const playerRef = useRef(null);
  const callbacksRef = useRef({ onReady, onStateChange, onError });
  const [failed, setFailed] = useState(false);
  const [status, setStatus] = useState("loading");
  const [failureKind, setFailureKind] = useState("embed");

  callbacksRef.current = { onReady, onStateChange, onError };

  useImperativeHandle(ref, () => ({
    getCurrentTime: () => Number(playerRef.current?.getCurrentTime?.() || 0),
    getDuration: () => Number(playerRef.current?.getDuration?.() || 0),
    getPlayerState: () => playerRef.current?.getPlayerState?.(),
    play: () => playerRef.current?.playVideo?.(),
    pause: () => playerRef.current?.pauseVideo?.(),
    seekTo: (seconds) => playerRef.current?.seekTo?.(Number(seconds) || 0, true),
    setVolume: (value) => playerRef.current?.setVolume?.(Number(value) || 0),
    setPlaybackRate: (rate) => playerRef.current?.setPlaybackRate?.(Number(rate) || 1),
  }), []);

  useEffect(() => {
    if (!videoId) return undefined;
    let cancelled = false;
    let initializationFailed = false;
    setFailed(false);
    setFailureKind("embed");
    setStatus("loading");
    const markFailed = (event) => {
      if (cancelled || initializationFailed) return;
      initializationFailed = true;
      setFailed(true);
      setFailureKind(String(event?.data || "").startsWith("api_") ? "sync" : "embed");
      setStatus("failed");
      callbacksRef.current.onError?.(event);
    };
    const timeoutId = window.setTimeout(() => markFailed({ data: "api_load_timeout" }), 15000);
    loadYouTubeApi().then((YT) => {
      if (cancelled || initializationFailed) return;
      playerRef.current = new YT.Player(hostId, {
        videoId,
        playerVars: { enablejsapi: 1, origin: window.location.origin, rel: 0, modestbranding: 1 },
        events: {
          onReady: () => {
            window.clearTimeout(timeoutId);
            setStatus("ready");
            callbacksRef.current.onReady?.();
          },
          onStateChange: (event) => callbacksRef.current.onStateChange?.(event.data),
          onError: markFailed,
        },
      });
    }).catch(() => markFailed({ data: "api_unavailable" }));
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      playerRef.current?.destroy?.();
      playerRef.current = null;
    };
  }, [hostId, videoId]);

  return (
    <div className="space-y-2">
      <div className="steel-video-frame relative overflow-hidden rounded-md" aria-busy={status === "loading"}>
        <div id={hostId} className="h-full w-full" />
        {status === "loading" && (
          <div className={`absolute inset-0 grid place-items-center text-sm ${variant === "light" ? "bg-slate-100 text-slate-600" : "bg-[#101219] text-gray-300"}`}>
            <span className="flex items-center gap-2"><Loader2 className={`h-4 w-4 animate-spin ${variant === "light" ? "text-teal-600" : "text-cyan-300"}`} /> Cargando video</span>
          </div>
        )}
      </div>
      {failed && (
        <p className={`flex items-start gap-2 rounded-md border p-2 text-sm ${variant === "light" ? "border-amber-200 bg-amber-50 text-amber-800" : "border-amber-500/30 bg-amber-500/10 text-amber-100"}`}>
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          {failureKind === "sync"
            ? "No se pudo iniciar el reproductor oficial; la sincronizacion no esta disponible."
            : "Este video no permite reproduccion embebida; el sync visual no puede continuar en la vista."}
        </p>
      )}
    </div>
  );
});

YouTubePracticePlayer.displayName = "YouTubePracticePlayer";

export default YouTubePracticePlayer;
